import { useEffect, useState } from "react";
import classNames from "classnames";
import { format } from "date-fns";
import {
  ComponentActions,
  UserPageRestriction,
  PageRestriction,
} from "../../../types/global";
import { Props } from "./interface";
import { usePageDataQuery } from "../../../hooks/usePageDataQuery";
import {
  Portal,
  ClientPortalPageRestrictionPermissions,
} from "../../../constants/user";
import { portalUserStatuses } from "../../../constants";
import {
  reactivatePortalUser,
  updateUserFolderPermissions,
} from "../../../loaders/pages/portalUserDetail";
import { Loading } from "../../../components/loading";
import { Routes } from "../../../constants/routes";
import { routeApis } from "../../../constants/apis";
import { RequestMethods } from "../../../constants/fetch";
import { portalUsersTableHeaders } from "../../../constants/tableHeaders";
import { FormContainer } from "../../../containers/form";
import { fetchWithTimeOut, setFetchOpts } from "../../../helpers/fetch";
import { isDefined } from "../../../helpers/utils";
import { FieldRenderers, FormConfig } from "../../../types/form";
import { ActionButton } from "../../../components/actionButton";
import { ErrorSummary } from "../../../components/forms/errorSummary";
import { BackButton } from "../../../components/backButton";
import { ConfirmHandler } from "../../../components/confirm";
import { Dialog } from "../../../components/dialog";
import { getPortalUserNotes } from "../../../loaders/pages/portalUserDetail";
import { USER_HAS_NO_FOLDER_PERMISSIONS } from "../../../constants/messages";

export interface FolderPermission {
  hasPermission: string;
  name: string;
}

/**
 * Template component for displaying and managing portal user details.
 * Handles user information display, page access restrictions, and user reactivation.
 *
 * @param {Props} props - Component props
 * @param {string} props.pageTitleText - Title text displayed at the top of the page
 * @param {string} props.saveText - Text for the save button
 * @param {object} props.apis - API endpoints configuration
 * @param {any} props.error - Initial error state
 * @param {Function} props.updateApi - Function to update user data
 * @param {string} props.azureUserId - Azure user ID for the current user
 * @param {object} props.user - User object containing all user details
 * @param {string[]} props.userFieldsOrder - Array defining the order of user fields to display
 * @param {string[]} props.hiddenUserFields - Array of field names to hide from display
 * @returns {JSX.Element} The rendered user management detail page
 */
export const UserManagementDetailPageTemplate = ({
  pageTitleText,
  saveText,
  apis,
  error: errorProp,
  updateApi,
  azureUserId,
  user,
  userFieldsOrder,
  hiddenUserFields,
}: Props): JSX.Element => {
  // State management
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [isProcessing, setIsProcessing] = useState<any>(false);
  const [emailChangeRequestNotes, setEmailChangeRequestNotes] = useState<
    Array<Record<string, any>>
  >([]);

  const [processErrorMessage, setProcessErrorMessage] = useState<any>();
  const [processSuccessMessage, setProcessSuccessMessage] =
    useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [
    shouldRenderReactivationConfirmation,
    setShouldRenderReactivationConfirmation,
  ] = useState<boolean>(false);
  const [pageAccessFormConfig, setPageAccessFormConfig] =
    useState<FormConfig | null>(null);
  const [folderAccessFormConfig, setFolderAccessFormConfig] =
    useState<FormConfig | null>(null);
  const [pageRestrictions, setPageRestrictions] =
    useState<Array<UserPageRestriction> | null>();

  // Extract user properties
  const { portal, status, folderPermissions } = user ?? {};

  /** Determines if the user is an active client portal user */
  const isActiveClientPortalUser =
    portal === Portal.ClientPortal &&
    (status === portalUserStatuses.Active ||
      status === portalUserStatuses.ResubmittedActive);

  /** Determines if the reactivation button should be enabled for this user */
  const shouldEnableReactivating =
    (status === portalUserStatuses.Rejected ||
      status === portalUserStatuses.Removed ||
      status === portalUserStatuses.ResubmittedRejected ||
      status === portalUserStatuses.ResubmittedRemoved) &&
    portal === Portal.ClientPortal;

  /** Determines if the page content should be rendered (form config loaded or non-active user) */
  const shouldRender: boolean =
    (Boolean(pageAccessFormConfig) && Boolean(pageRestrictions)) ||
    !isActiveClientPortalUser;

  /** Filters email change requests that have been approved */
  const approvedEmailChanges: Array<Record<string, any>> =
    emailChangeRequestNotes?.filter(
      (note) => note.userChangeStatus === "Approved"
    ) ?? [];

  /** Filters email change requests that have been rejected */
  const rejectedEmailChanges: Array<Record<string, any>> =
    emailChangeRequestNotes?.filter(
      (note) => note.userChangeStatus === "Rejected"
    ) ?? [];

  const hasFolderPermissions: boolean =
    folderPermissions?.folderPermissionsJson?.length > 0;
  const folderPermissionsList: Array<FolderPermission> = hasFolderPermissions
    ? JSON.parse(folderPermissions?.folderPermissionsJson)?.folders ?? []
    : [];

  /**
   * Handles the reactivation of a portal user.
   * Calls the API to reactivate the user and updates the UI based on the response.
   *
   * @param {any} data - Data object containing the azureUserId
   * @returns {Promise<void>}
   */
  const reactivateUserHandler = async (data: any): Promise<void> => {
    setProcessErrorMessage(undefined);
    setProcessSuccessMessage("");
    setIsProcessing(true);

    const { error } = await reactivatePortalUser(data);

    setIsProcessing(false);
    setIsDialogOpen(true);

    if (error) {
      setProcessErrorMessage(error);
    } else {
      setShouldRenderReactivationConfirmation(false);
      setProcessSuccessMessage("Portal user reactivated successfully");
    }
  };

  /**
   * Handler for canceling the reactivation confirmation dialog.
   * Hides the confirmation UI.
   */
  const reactivationConfirmationCancelHandler: Function = () =>
    setShouldRenderReactivationConfirmation(false);

  /**
   * Handler for accepting the reactivation confirmation dialog.
   * Proceeds with reactivating the user.
   */
  const reactivationConfirmationAcceptHandler: Function = async () => {
    reactivateUserHandler({ azureUserId });
  };

  // Custom hook for page data queries
  const { pageDataRequestError, onGet } = usePageDataQuery(
    {
      formValues,
      initialError: errorProp,
      apis,
    },
    { setFormValues }
  );

  /**
   * Generates the form configuration for page access restrictions.
   * Fetches user page restrictions and available page restrictions,
   * then creates a form config with select fields for each page.
   *
   * @param {string} id - The Azure user ID
   * @returns {Promise<void>}
   */
  const generatePageAccessFormConfig = async (
    azureUserId: string,
    portalHubPageCmsId: string
  ) => {
    const fetchOptions = setFetchOpts({
      method: RequestMethods.GET,
    });

    try {
      const userPageRestrictionsApiUrl: string = `${
        routeApis[Routes.PORTAL_USERS].userPageRestrictions
      }?userId=${azureUserId}`;
      const pageRestrictionsApiUrl: string = portalHubPageCmsId
        ? `${
            routeApis[Routes.CLIENT_PORTAL_PAGE_RESTRICTIONS]
              .getPageRestrictions
          }?portalHubPageCmsId=${portalHubPageCmsId}`
        : routeApis[Routes.CLIENT_PORTAL_PAGE_RESTRICTIONS].getPageRestrictions;

      const response = await Promise.all([
        fetchWithTimeOut(userPageRestrictionsApiUrl, fetchOptions, 10000),
        fetchWithTimeOut(pageRestrictionsApiUrl, fetchOptions, 10000),
      ]);

      if (response[0].ok && response[1].ok) {
        const pageRestrictionsData: Array<PageRestriction> =
          await response[1].json();
        const userPageRestrictions: Array<UserPageRestriction> =
          await response[0].json();

        setPageRestrictions(userPageRestrictions);

        const generatedPageAccessFormConfig: FormConfig = {
          fields: pageRestrictionsData.map(
            (pageRestriction: PageRestriction) => {
              const userRestriction: UserPageRestriction | undefined =
                userPageRestrictions.find(
                  (item: UserPageRestriction) =>
                    item.pageRestrictionId === pageRestriction.restrictionId
                );

              return {
                // id in final form cannot be numeric, must contain alpha characters.
                id: `restriction:${pageRestriction.restrictionId}`,
                validators: [],
                label: pageRestriction.pageName,
                isLabelHidden: false,
                initialValue:
                  userRestriction?.restrictionPermission ||
                  ClientPortalPageRestrictionPermissions.NoAccess,
                renderer: FieldRenderers.SELECT,
                options: [
                  {
                    value: ClientPortalPageRestrictionPermissions.NoAccess,
                    displayText: "No Access",
                  },
                  {
                    value: ClientPortalPageRestrictionPermissions.ReadOnly,
                    displayText: "Read Only",
                  },
                  ...(pageRestriction.hasReadWriteOption
                    ? [
                        {
                          value:
                            ClientPortalPageRestrictionPermissions.ReadWrite,
                          displayText: "Read/Write",
                        },
                      ]
                    : []),
                ],
                classes: {
                  label: "o-form-label u-text-sm",
                  wrapper: "u-flex u-flex-row-reverse u-justify-end u-gap-2",
                },
              };
            }
          ),
        };

        setPageAccessFormConfig(generatedPageAccessFormConfig);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Generates the form configuration for folder access restrictions.
   * Fetches user folder restrictions and available folder restrictions,
   * then creates a form config with select fields for each folder.
   *
   * @param {string} id - The Azure user ID
   * @returns {Promise<void>}
   */
  const generateFolderAccessFormConfig = async (
    folderPermissionsList: Array<FolderPermission>
  ) => {
    // Helper to convert folder name to a valid hyphenated ID
    const toHyphenatedId = (name: string): string => {
      return name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
    };

    try {
      // Get the hyphenated IDs of folders where hasPermission is 'true'
      const initialCheckedValues: Array<string> = folderPermissionsList
        .filter((folder) => folder.hasPermission === "true")
        .map((folder) => toHyphenatedId(folder.name));

      const generatedFolderAccessFormConfig: FormConfig = {
        fields: [
          {
            id: `allowed-folders`,
            validators: [],
            label: "Allowed folders",
            isLabelHidden: true,
            initialValue: initialCheckedValues,
            renderer: FieldRenderers.CHECKBOX_LIST,
            options: folderPermissionsList.map(
              (folderPermission: FolderPermission) => {
                return {
                  value: toHyphenatedId(folderPermission.name),
                  displayText: folderPermission.name,
                };
              }
            ),
            classes: {
              optionsWrapper: "u-columns-2",
            },
          },
        ],
      };

      setFolderAccessFormConfig(generatedFolderAccessFormConfig);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Handles the submission of the portal page access form.
   * Updates user page restrictions and displays success/error messages.
   *
   * @param {Array<UserPageRestriction>} pageRestrictions - Current page restrictions for the user
   * @returns {Function} Form submit handler function
   */
  const handlePortalPageAccessFormSubmit =
    (pageRestrictions: Array<UserPageRestriction>) =>
    async (modalFormValues: Record<string, string>) => {
      setIsProcessing(true);

      const response = await updateApi({
        formValues: modalFormValues,
        pageRestrictions,
        item: user,
      });

      const { error } = response;

      setIsProcessing(false);
      setIsDialogOpen(true);

      if (error) {
        return {
          hasSubmitError: true,
          submitError: error,
        };
      } else {
        setProcessSuccessMessage("Portal page access updated successfully");
        onGet({
          formValues: formValues,
        });
        // resolve to satisfy final form
        return {};
      }
    };

  /**
   * Handles the submission of the portal folder access form.
   * Updates user folder access and displays success/error messages.
   *
   * @returns {Function} Form submit handler function
   */
  const handlePortalFolderAccessFormSubmit =
    () => async (modalFormValues: Record<string, string>) => {
      setIsProcessing(true);

      const response = await updateUserFolderPermissions({
        formValues: modalFormValues,
        folderPermissionsList,
        folderPermissions,
      });

      const { error } = response;

      setIsProcessing(false);
      setIsDialogOpen(true);

      if (error) {
        return {
          hasSubmitError: true,
          submitError: error,
        };
      } else {
        setProcessSuccessMessage("Portal folder access updated successfully");
        onGet({
          formValues: formValues,
        });
        // resolve to satisfy final form
        return {};
      }
    };

  /**
   * Fetches and sets email change request notes for the given user.
   * Retrieves both approved and rejected email change requests.
   *
   * @param {string} userId - The Azure user ID
   * @returns {Promise<void>}
   */
  const getPortalUserEmailChangeRequestNotes = async (userId: string) => {
    const response = await getPortalUserNotes({ userId });

    const { error, data: notes } = response;

    if (!error) {
      setEmailChangeRequestNotes(notes);
    }
  };

  // Prepare user data display (remove hidden fields if needed)
  const { ...displayItem } = user ?? {};

  // Define action buttons for the form
  const actions: ComponentActions = [
    {
      text: saveText,
      props: {
        type: "submit",
      },
    },
  ];

  /**
   * Closes the confirmation/success dialog.
   */
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  // Transform action buttons if not an active client portal user
  if (!isActiveClientPortalUser) {
    actions.pop();
  }

  /** CSS class names for various UI elements */
  const generatedClasses: Record<string, string> = {
    buttonWrapper: classNames("u-flex u-justify-end u-mb-6"),
    button: classNames(
      "u-bg-teal-green u-text-white u-border-none u-p-0 u-rounded-xl u-my-2 u-flex u-items-center"
    ),
    svgButton: classNames(
      "u-bg-transparent u-border-none u-p-0 u-m-0 u-text-fiord-blue u-py-2 u-flex u-items-center"
    ),
    item: classNames(
      "tablet:u-columns-2 u-m-0 u-border-0 u-border-b u-border-solid u-border-white"
    ),
    dlItem: classNames("u-flex u-gap-2 u-flex-wrap u-my-2"),
    itemTitle: classNames("u-font-bold"),
    itemText: classNames("u-p-0 u-m-0"),
    formWrapper: classNames("c-form u-my-4"),
    fieldsWrapper: classNames("tablet:u-columns-2"),
    fieldWrapper: classNames(
      "o-form-item u-flex u-flex-col u-flex-wrap u-gap-2 u-break-inside-avoid"
    ),
    actionButtonWrapper: classNames(
      "u-flex u-mt-4 u-gap-2 u-flex-wrap u-justify-center"
    ),
  };

  /**
   * Effect hook that runs when the azureUserId changes.
   * Generates form config for active client portal users and fetches email change request notes.
   */
  useEffect(() => {
    if (isDefined(azureUserId) && isActiveClientPortalUser)
      generatePageAccessFormConfig(azureUserId, user.portalHubPageCmsId);
    generateFolderAccessFormConfig(folderPermissionsList);
    getPortalUserEmailChangeRequestNotes(azureUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureUserId]);

  return (
    <>
      <div className="u-bg-burnham-green u-text-center u-text-white u-m-0 u-p-2 u-to-viewport-edge-x">
        <h2>{pageTitleText}</h2>
      </div>
      <div className="u-flex u-flex-col u-justify-center u-my-5 u-w-full u-overflow-x-auto">
        {pageDataRequestError ? (
          <>{pageDataRequestError}</>
        ) : shouldRender ? (
          <div className="u-p-3 u-pb-0 u-mb-6">
            <BackButton className="u-w-fit u-mb-6">
              Back to Portal Users
            </BackButton>
            <section className="u-mb-6 u-p-6 u-border-black-transparent-01 u-border u-border-solid">
              <h3 className="u-p-0 u-m-0">User details</h3>
              <div className="u-my-2">
                <dl className={generatedClasses.item}>
                  {userFieldsOrder?.map((key, index) => {
                    if (hiddenUserFields?.includes(key)) {
                      return null;
                    }

                    return (
                      <div className={generatedClasses.dlItem} key={index}>
                        <dt className={generatedClasses.itemTitle}>
                          {portalUsersTableHeaders[key] ?? key}:
                        </dt>
                        <dd className={generatedClasses.itemText}>{`${
                          displayItem[key] ?? ""
                        }`}</dd>
                      </div>
                    );
                  })}
                </dl>
                {shouldEnableReactivating && (
                  <div className="u-mt-4">
                    <ActionButton
                      className="u-rounded-none u-w-fit"
                      callback={() =>
                        setShouldRenderReactivationConfirmation(true)
                      }
                      isDisabled={shouldRenderReactivationConfirmation}
                    >
                      Reactivate B2C account
                    </ActionButton>
                    {shouldRenderReactivationConfirmation && (
                      <ConfirmHandler
                        className="u-mt-2 u-max-w-fit"
                        confirmationText={
                          "Are you sure you want to reactivate this user?"
                        }
                        acceptAction={reactivationConfirmationAcceptHandler}
                        cancelAction={reactivationConfirmationCancelHandler}
                      />
                    )}
                  </div>
                )}
                {(approvedEmailChanges?.length > 0 ||
                  rejectedEmailChanges?.length > 0) && (
                  <div className="u-bg-mercury-grey u-py-2 u-my-2 u-px-1 u-border-y-2 u-border-x-0 u-border-solid u-border-y-edward-grey">
                    <h4 className="u-m-0">Email change request</h4>
                    {approvedEmailChanges?.length > 0 && (
                      <div className="u-ml-4 u-mb-4">
                        <h4 className="u-m-0 u-my-2 u-font-bold">
                          Approval(s)
                        </h4>
                        <div className="u-ml-3">
                          {approvedEmailChanges.map(
                            (approval, index: number) => (
                              <dl className={generatedClasses.item} key={index}>
                                <div className={generatedClasses.dlItem}>
                                  <dt className={generatedClasses.itemTitle}>
                                    By:
                                  </dt>
                                  <dd className={generatedClasses.itemText}>
                                    {approval?.authorEmailAddress}
                                  </dd>
                                </div>
                                <div className={generatedClasses.dlItem}>
                                  <dt className={generatedClasses.itemTitle}>
                                    Date & time:
                                  </dt>
                                  <dd className={generatedClasses.itemText}>
                                    {approval?.dateTime &&
                                      format(
                                        new Date(approval?.dateTime),
                                        "kk:mm P"
                                      )}
                                  </dd>
                                </div>
                                {approval?.note && (
                                  <div className={generatedClasses.dlItem}>
                                    <dt className={generatedClasses.itemTitle}>
                                      Reason:
                                    </dt>
                                    <dd className={generatedClasses.itemText}>
                                      {approval?.note}
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            )
                          )}
                        </div>
                      </div>
                    )}
                    {rejectedEmailChanges?.length > 0 && (
                      <div className="u-ml-4">
                        <h4 className="u-m-0 u-my-2 u-font-bold">
                          Rejection(s)
                        </h4>
                        <div className="u-ml-3">
                          {rejectedEmailChanges.map(
                            (rejection, index: number) => (
                              <dl className={generatedClasses.item} key={index}>
                                <div className={generatedClasses.dlItem}>
                                  <dt className={generatedClasses.itemTitle}>
                                    By:
                                  </dt>
                                  <dd className={generatedClasses.itemText}>
                                    {rejection?.authorEmailAddress}
                                  </dd>
                                </div>
                                <div className={generatedClasses.dlItem}>
                                  <dt className={generatedClasses.itemTitle}>
                                    Date & time:
                                  </dt>
                                  <dd className={generatedClasses.itemText}>
                                    {rejection?.dateTime &&
                                      format(
                                        new Date(rejection?.dateTime),
                                        "kk:mm P"
                                      )}
                                  </dd>
                                </div>
                                {rejection?.note && (
                                  <div className={generatedClasses.dlItem}>
                                    <dt className={generatedClasses.itemTitle}>
                                      Reason:
                                    </dt>
                                    <dd className={generatedClasses.itemText}>
                                      {rejection?.note}
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
            {isActiveClientPortalUser ? (
              <>
                <section className="u-mb-6 u-p-6 u-border-black-transparent-01 u-border u-border-solid">
                  <h3 className={generatedClasses.itemTitle}>
                    Remove or Add Client Portal page access{" "}
                  </h3>
                  <div>
                    <FormContainer
                      {...pageAccessFormConfig}
                      classNames={{
                        formWrapper: generatedClasses.formWrapper,
                        fieldsWrapper: generatedClasses.fieldsWrapper,
                        fieldWrapper: generatedClasses.fieldWrapper,
                      }}
                      onSubmitAction={handlePortalPageAccessFormSubmit(
                        pageRestrictions!
                      )}
                    >
                      <div className={generatedClasses.actionButtonWrapper}>
                        {actions?.map(({ text, callback, props }, index) => (
                          <ActionButton
                            className="u-rounded-none u-w-fit"
                            {...props}
                            callback={() => callback?.(azureUserId)}
                            key={index}
                          >
                            <>{text}</>
                          </ActionButton>
                        ))}
                      </div>
                    </FormContainer>
                  </div>
                </section>
                <section className="u-p-6 u-border-black-transparent-01 u-border u-border-solid">
                  <h3 className="u-p-0 u-m-0">Folder access </h3>
                  {!hasFolderPermissions ? (
                    <p>{USER_HAS_NO_FOLDER_PERMISSIONS}</p>
                  ) : (
                    <FormContainer
                      {...folderAccessFormConfig}
                      classNames={{
                        formWrapper: generatedClasses.formWrapper,
                        fieldsWrapper: "",
                        fieldWrapper: generatedClasses.fieldWrapper,
                      }}
                      onSubmitAction={handlePortalFolderAccessFormSubmit()}
                    >
                      <div className={generatedClasses.actionButtonWrapper}>
                        {actions?.map(({ text, callback, props }, index) => (
                          <ActionButton
                            className="u-rounded-none u-w-fit"
                            {...props}
                            callback={() => callback?.(azureUserId)}
                            key={index}
                          >
                            <>{text}</>
                          </ActionButton>
                        ))}
                      </div>
                    </FormContainer>
                  )}
                </section>
              </>
            ) : (
              <>
                <div className={generatedClasses.actionButtonWrapper}>
                  {actions?.map(({ text, callback, props }, index) => (
                    <ActionButton
                      className="u-rounded-none u-w-fit"
                      {...props}
                      callback={() => callback?.(azureUserId)}
                      key={index}
                    >
                      <>{text}</>
                    </ActionButton>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <Loading isActive={true} titleText="Loading content..." />
        )}
        <Dialog
          id="user-management-confirmation-dialog"
          isOpen={isDialogOpen}
          handleCloseDialog={handleCloseDialog}
        >
          {processSuccessMessage && <div>{processSuccessMessage}</div>}
          {processErrorMessage && (
            <ErrorSummary globalError={processErrorMessage}></ErrorSummary>
          )}
        </Dialog>
        {
          <Loading
            isActive={isProcessing}
            titleText="Updating..."
            className="u-absolute u-top-0 u-right-0 u-bottom-0 u-left-0 u-bg-white-06"
            bodyClassName="u-border-black-transparent-01 u-border u-border-solid"
          />
        }
      </div>
    </>
  );
};
